import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '../../hooks/useApiClient';
import { useToast } from '../../contexts/ToastContext';
import { PageError } from '@/components/ui';
import SupportTicketForm from '../../components/SupportTicketForm';
import SupportTicketList from '../../components/SupportTicketList';
import { SupportTicket } from '@haccp/api-client';

type StatusFilter = 'ALL' | 'OPEN' | 'IN_PROGRESS' | 'CLOSED';

export default function SupportPage() {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [showForm, setShowForm] = useState(false);

  // Auto-refresh interval (30 seconds)
  const REFRESH_INTERVAL = 30 * 1000;

  // Fetch support tickets
  const { data: ticketsData, isLoading, error } = useQuery({
    queryKey: ['support:tickets'],
    queryFn: async () => {
      return apiClient.getSupportTickets();
    },
    refetchInterval: REFRESH_INTERVAL,
  });

  // Create support ticket mutation
  const createMutation = useMutation({
    mutationFn: async (variables: { category: string; message: string }) => {
      return apiClient.createSupportTicket(variables.category, variables.message);
    },
    onSuccess: () => {
      addToast(t('support.sent'), 'success');
      setShowForm(false);
      queryClient.invalidateQueries({ queryKey: ['support:tickets'] });
    },
    onError: () => {
      addToast(t('support.createError'), 'error');
    },
  });

  const tickets = (ticketsData as SupportTicket[]) || [];

  // Filter tickets
  const filteredTickets = tickets.filter((ticket: SupportTicket) => {
    if (statusFilter === 'OPEN') return ticket.status === 'OPEN';
    if (statusFilter === 'IN_PROGRESS') return ticket.status === 'IN_PROGRESS';
    if (statusFilter === 'CLOSED') return ticket.status === 'CLOSED';
    return true;
  });

  // Calculate stats
  const openCount = tickets.filter((t: SupportTicket) => t.status === 'OPEN').length;
  const inProgressCount = tickets.filter((t: SupportTicket) => t.status === 'IN_PROGRESS').length;
  const closedCount = tickets.filter((t: SupportTicket) => t.status === 'CLOSED').length;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900">{t('support.title')}</h1>
          <p className="text-neutral-600 mt-2">{t('support.description')}</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="w-full sm:w-auto px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
        >
          {showForm ? t('support.cancel') : t('support.newTicket')}
        </button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="mb-8 bg-white rounded-lg border border-neutral-200 p-4 sm:p-6">
          <h2 className="text-lg font-bold text-neutral-900 mb-4">{t('support.createTicket')}</h2>
          <SupportTicketForm
            isLoading={createMutation.isPending}
            onSubmit={(category, message) => {
              createMutation.mutate({ category, message });
            }}
          />
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <div className="text-sm font-medium text-neutral-600">{t('support.totalTickets')}</div>
          <div className="text-3xl font-bold text-neutral-900 mt-2">{tickets.length}</div>
        </div>
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <div className="text-sm font-medium text-blue-600">{t('support.open')}</div>
          <div className="text-3xl font-bold text-blue-700 mt-2">{openCount}</div>
        </div>
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <div className="text-sm font-medium text-yellow-600">{t('support.inProgress')}</div>
          <div className="text-3xl font-bold text-yellow-700 mt-2">{inProgressCount}</div>
        </div>
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <div className="text-sm font-medium text-green-600">{t('support.resolved')}</div>
          <div className="text-3xl font-bold text-green-700 mt-2">{closedCount}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-2 flex-wrap">
        <button
          onClick={() => setStatusFilter('ALL')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === 'ALL'
              ? 'bg-blue-50 text-blue-700 border border-blue-200'
              : 'bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50'
          }`}
        >
          {t('support.filterAll')}
        </button>
        <button
          onClick={() => setStatusFilter('OPEN')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === 'OPEN'
              ? 'bg-blue-50 text-blue-700 border border-blue-200'
              : 'bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50'
          }`}
        >
          {t('support.filterOpen')} ({openCount})
        </button>
        <button
          onClick={() => setStatusFilter('IN_PROGRESS')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === 'IN_PROGRESS'
              ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
              : 'bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50'
          }`}
        >
          {t('support.filterInProgress')} ({inProgressCount})
        </button>
        <button
          onClick={() => setStatusFilter('CLOSED')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === 'CLOSED'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50'
          }`}
        >
          {t('support.filterClosed')} ({closedCount})
        </button>
      </div>

      {/* Tickets List */}
      {error ? (
        <PageError message={t('support.loadError')} />
      ) : (
        <div className="bg-white rounded-lg border border-neutral-200">
          <SupportTicketList tickets={filteredTickets} isLoading={isLoading} />
        </div>
      )}

      {/* Help Section */}
      <div className="mt-8 bg-blue-50 rounded-lg border border-blue-200 p-4 sm:p-6">
        <h3 className="text-lg font-bold text-blue-900 mb-3">{t('support.urgentHelp')}</h3>
        <p className="text-blue-800">
          {t('support.urgentHelpDescription')}
        </p>
      </div>
    </div>
  );
}
